import { ApiError } from "@allergy123/api-client";
import { useApi, useLoad } from "@allergy123/api-client/react";
import type { OrganizationEntitlementSummary, OrganizationSummary } from "@allergy123/contracts";
import { Button, Card, Field, Status, Table, Td, Th } from "@allergy123/ui";
import { useState } from "react";
import { Link } from "react-router";
import { endedNote, endQuestion, otherEntitlementFor, PRODUCT_LABELS, type ConsoleProduct } from "./access";

const PRODUCTS = {
  learn: { label: PRODUCT_LABELS.learn, granted: "Learning access granted. Active members can now use training." },
  clinical: { label: PRODUCT_LABELS.clinical, granted: "Clinical access granted. Give each member clinical permissions on Members; nobody has any yet." },
} as const satisfies Record<ConsoleProduct, { label: string; granted: string }>;
const consoleProduct = (e: OrganizationEntitlementSummary): ConsoleProduct | undefined => (e.product === "learn" || e.product === "clinical" ? e.product : undefined);

/**
 * First admin workflow (ADR 0011): organizations, their size, and product access, granted and
 * ended here. Ending takes effect on the members' next request, unless the organization holds
 * another current entitlement for the product, which the confirm step and the note then say. An
 * entitlement billing wrote from a Stripe subscription is billing's to end (ADR 0008) and shows no control.
 */
export function OrganizationsPage() {
  const api = useApi();
  const data = useLoad((a) => a.admin.organizations());
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [granting, setGranting] = useState<string | null>(null);
  /** The entitlement whose end is being confirmed, and whether the request is in flight. */
  const [ending, setEnding] = useState<{ organization: OrganizationSummary; entitlement: OrganizationEntitlementSummary; product: ConsoleProduct; busy: boolean } | null>(null);

  async function grant(organizationId: string, product: keyof typeof PRODUCTS) {
    setGranting(`${organizationId}:${product}`);
    setNote(null);
    try {
      await api.admin.grant({ product, subjectType: "organization", subjectId: organizationId });
      setNote(PRODUCTS[product].granted);
      data.reload();
    } catch (err) {
      setNote(`Couldn't grant ${PRODUCTS[product].label} (${err instanceof Error ? err.message : "error"}).`);
    } finally {
      setGranting(null);
    }
  }

  async function end() {
    if (!ending || ending.busy) return;
    setEnding({ ...ending, busy: true });
    setNote(null);
    try {
      await api.admin.endEntitlement(ending.entitlement.id);
      setNote(endedNote(ending.product, ending.organization.name, otherEntitlementFor(ending.organization.entitlements, ending.entitlement)));
      setEnding(null);
      data.reload();
    } catch (err) {
      const code = err instanceof ApiError ? err.code : undefined;
      setNote(code === "managed_by_billing" ? `Couldn't end ${PRODUCTS[ending.product].label}: billing manages it through the Stripe subscription.` : `Couldn't end ${PRODUCTS[ending.product].label} (${err instanceof Error ? err.message : "error"}).`);
      setEnding({ ...ending, busy: false });
    }
  }

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setNote(null);
    try {
      const res = await api.admin.createOrganization(name.trim());
      setNote(`Created ${res.organization.name}.`);
      setName("");
      data.reload();
    } catch (err) {
      setNote(`Couldn't create the organization (${err instanceof Error ? err.message : "error"}).`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="mb-8">
        <h1 className="mb-2 text-h1 font-semibold text-anchor">Organizations</h1>
        <p className="max-w-[68ch] text-ink-secondary">Practices enrolled on the platform, their locations, and who belongs to them. Every change here is audited.</p>
      </div>
      {data.state === "error" &&
        (data.error instanceof ApiError && data.error.code === "mfa_required" ? (
          <Card tone="tinted" className="mb-6">
            <p className="mb-4 max-w-[60ch]">Turn on two-step verification to use the console. It takes a minute with an authenticator app.</p>
            <Button href="/security">Set up two-step verification</Button>
          </Card>
        ) : (
          <Status kind="error" label={`Couldn't load organizations (${data.error?.message})`} />
        ))}
      {note && <div className="mb-6"><Status kind="info" label={note} /></div>}
      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <Card>
          <Table caption={data.data ? `${data.data.organizations.length} organization${data.data.organizations.length === 1 ? "" : "s"}` : "Loading…"}>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Locations</Th>
                <Th>Members</Th>
                <Th>Products</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {data.data?.organizations.length === 0 && (
                <tr>
                  <Td colSpan={5} className="text-ink-secondary">
                    When the first practice is enrolled it appears here with its locations, members, and product access.
                  </Td>
                </tr>
              )}
              {data.data?.organizations.map((o) => (
                <tr key={o.id}>
                  <Td>
                    <div className="font-medium">{o.name}</div>
                    <div className="text-small text-ink-secondary">{o.slug}</div>
                    <div className="flex gap-3 text-small">
                      <Link to={`/organizations/${o.id}/locations`} className="text-anchor underline">Locations</Link>
                      <Link to={`/organizations/${o.id}/members`} className="text-anchor underline">Members</Link>
                      <Link to={`/organizations/${o.id}/billing`} className="text-anchor underline">Billing</Link>
                    </div>
                  </Td>
                  <Td>{o.locations}</Td>
                  <Td>{o.members}</Td>
                  <Td>
                    <div>{o.products.length ? o.products.join(", ") : <span className="text-ink-secondary">None</span>}</div>
                    {o.status === "active" && (
                      <div className="flex flex-wrap gap-2">
                        {!o.products.includes("learn") && (
                          <Button variant="secondary" surface="card" onClick={() => void grant(o.id, "learn")} disabled={granting !== null}>
                            {granting === `${o.id}:learn` ? "Granting…" : "Grant learning access"}
                          </Button>
                        )}
                        {!o.products.includes("clinical") && (
                          <Button variant="secondary" surface="card" onClick={() => void grant(o.id, "clinical")} disabled={granting !== null}>
                            {granting === `${o.id}:clinical` ? "Granting…" : "Grant clinical access"}
                          </Button>
                        )}
                        {o.entitlements.map((e) => {
                          const product = consoleProduct(e);
                          if (!product) return null;
                          if (e.source === "stripe") return <span key={e.id} className="self-center text-small text-ink-secondary">{PRODUCTS[product].label}: managed by billing</span>;
                          return (
                            <Button key={e.id} variant="secondary" surface="card" onClick={() => setEnding({ organization: o, entitlement: e, product, busy: false })} disabled={granting !== null || ending !== null}>
                              End {PRODUCTS[product].label}
                            </Button>
                          );
                        })}
                      </div>
                    )}
                    {ending?.entitlement.id && o.entitlements.some((e) => e.id === ending.entitlement.id) && (
                      <div role="group" aria-label={`End ${PRODUCTS[ending.product].label} for ${o.name}`} className="mt-3 grid gap-3 rounded-sm border border-rule-strong p-3">
                        <p className="text-small">{endQuestion(ending.product, o.name, otherEntitlementFor(o.entitlements, ending.entitlement))}</p>
                        <div className="flex flex-wrap gap-2">
                          <Button onClick={() => void end()} disabled={ending.busy}>{ending.busy ? "Ending…" : `End ${PRODUCTS[ending.product].label}`}</Button>
                          <Button variant="secondary" surface="card" onClick={() => setEnding(null)} disabled={ending.busy}>Keep it</Button>
                        </div>
                      </div>
                    )}
                  </Td>
                  <Td>{o.status}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
        <Card as="section">
          <h2 className="mb-4 text-h3 font-semibold text-anchor">Add an organization</h2>
          <form onSubmit={(e) => void create(e)} className="grid gap-4">
            <Field label="Practice name" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} wide hint="The address slug is derived from the name." />
            <div>
              <Button type="submit" disabled={busy || name.trim().length < 2}>
                {busy ? "Creating…" : "Create organization"}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </>
  );
}
